import styled from "styled-components";

export const ModalBox = styled.div`
  position: relative;
  z-index: 1;
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 520px;
  max-width: 95vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
`;

export const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

export const DropZone = styled.div<{ isDragging: boolean }>`
  border: 2px dashed ${({ isDragging }) => (isDragging ? "#667eea" : "#d0d5dd")};
  border-radius: 8px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ isDragging }) => (isDragging ? "#f0f2ff" : "#fafafa")};

  &:hover {
    border-color: #667eea;
    background: #f0f2ff;
  }
`;

export const DropIcon = styled.div`
  font-size: 32px;
  line-height: 1;
`;

export const DropText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #444;
  text-align: center;
`;

export const DropLink = styled.span`
  color: #667eea;
  font-weight: 500;
  text-decoration: underline;
`;

export const DropHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #999;
`;

export const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f0f2ff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  font-size: 13px;
  color: #3730a3;
`;

export const FileIcon = styled.span`
  font-size: 18px;
`;

export const FileName = styled.span`
  flex: 1;
  font-weight: 500;
  word-break: break-all;
`;

export const ClearFileButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    color: #ef4444;
  }
`;

export const ErrorBlock = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 13px;
  color: #b91c1c;
`;

export const ErrorTitle = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
`;

export const ErrorList = styled.ul`
  margin: 6px 0 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const UploadButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 12px;
  background: ${({ disabled }) =>
    disabled ? "#d1d5db" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
  }
`;

export const ExampleLink = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #374151;
  }
`;

export const AdvancedLink = styled.button`
  background: none;
  border: none;
  color: #667eea;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-align: center;
  width: 100%;
  text-decoration: underline;

  &:hover {
    color: #4f46e5;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
  padding: 4px;

  &:hover {
    color: #374151;
  }
`;

export const ErrorHint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #7f1d1d;
  font-style: italic;
`;

const TYPE_BADGE_COLORS: Record<string, { bg: string; fg: string }> = {
  container: { bg: "#dbeafe", fg: "#1e40af" },
  firmware: { bg: "#fef3c7", fg: "#92400e" },
  host: { bg: "#d1fae5", fg: "#065f46" },
};

export const ComponentTypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ $type }) =>
    (TYPE_BADGE_COLORS[$type] || { bg: "#e5e7eb" }).bg};
  color: ${({ $type }) => (TYPE_BADGE_COLORS[$type] || { fg: "#374151" }).fg};
  margin-left: 6px;
  vertical-align: middle;
`;

export const SummaryBlock = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 13px;
  color: #166534;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SummaryTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

export const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SummarySectionTitle = styled.div`
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #15803d;
`;

export const SummaryList = styled.ul`
  margin: 2px 0 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #166534;
`;

export const DoneButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(34, 197, 94, 0.4);
  }
`;

export const ComponentRow = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 0;
  border-bottom: 1px solid #d1fae5;
  list-style: none;

  &:last-child {
    border-bottom: none;
  }
`;

export const ComponentRowName = styled.span`
  flex: 1;
  font-weight: 500;
  color: #166534;
`;

export const ComponentRowVersion = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: #374151;
  background: #e5e7eb;
  border-radius: 3px;
  padding: 1px 5px;
`;

export const StatusBadge = styled.span<{ $status: "created" | "found" }>`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === "created" ? "#dcfce7" : "#dbeafe"};
  color: ${({ $status }) => ($status === "created" ? "#15803d" : "#1e40af")};
  flex-shrink: 0;
`;

export const ComponentTable = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;
